import * as React from "react";
import {
	Chip,
	CircularProgress,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { LockIcon } from "@phosphor-icons/react/dist/ssr/Lock";

import { usePreparationOptions } from "@/hooks/api/use-campaign-preparation";
import type { CampaignRequestDetail, CampaignRequestMarket } from "@/hooks/api/use-campaign-requests";
import { useUpdateCampaignRequestMarket } from "@/hooks/api/use-campaign-requests";

import { HelpTooltip } from "../../components";
import { marketFlag, marketLabel } from "../../constants";
import { AccountPageLink } from "./account-page-link";

/**
 * The two bindings UX §15 counts on its readiness checklist and nothing on the screen could set.
 *
 * "4 local numbers reserved" and "3,140 leads routed to 4 market groups" were reported by that
 * checklist and produced by nobody: preparation was read-only, so both had to be arranged
 * somewhere else and the screen described a state it could not reach. This is the part of that
 * screen that can genuinely work today, so it works — the caller ID and the contact group are
 * both plain columns on `campaign_request_market`, both already accepted by
 * `PUT /campaign-requests/:request/markets/:market`, and both read straight off the market row by
 * provisioning when it builds the campaign.
 *
 * What is deliberately *not* here is anything that would have to be invented. Importing a lead
 * file into contacts and cutting it into per-market groups is the deterministic pipeline that
 * only counts today and does not write; that gap is stated once on the Leads tab rather than
 * papered over with a control here. This screen binds an existing group; it does not pretend to
 * create one.
 *
 * A market that has already been provisioned is read-only, and says why. Provisioning is
 * idempotent by market — a second run skips any market that already has a `campaignId` — so
 * changing the row afterwards would save happily and change nothing about the campaign that is
 * about to dial. That is precisely the control-wired-to-nothing failure this screen is being
 * cleared of, so it is disabled and points at the campaign, where the binding now lives.
 */

/** How many of each list to offer. Both are per-workspace and small; neither is a search field. */

export interface MarketBindingsProps {
	request: CampaignRequestDetail;
	/** Ordered by position — the client's own market order, lead market first. */
	markets: CampaignRequestMarket[];
}

export function MarketBindings({ request, markets }: MarketBindingsProps): React.JSX.Element {
	const { data: options, isPending, isError } = usePreparationOptions(request.id);
	const isNumbersPending = isPending,
		isGroupsPending = isPending;
	const updateMarket = useUpdateCampaignRequestMarket();

	// Which row is in flight, so only that row's controls go busy rather than all of them.
	const [saving, setSaving] = React.useState<string | null>(null);

	const save = async (market: CampaignRequestMarket, data: Partial<CampaignRequestMarket>): Promise<void> => {
		setSaving(market.id);
		try {
			await updateMarket.mutateAsync({ requestId: request.id, marketId: market.id, data });
		} catch {
			/* the mutation reports the API's own sentence as a toast */
		} finally {
			setSaving(null);
		}
	};

	const numberOptions = options?.numbers ?? [];
	const groupOptions = options?.groups ?? [];

	return (
		<Stack spacing={2}>
			{/* One line each, pointing at the screen that fixes it. Both of these used to be a
			    three-sentence amber panel explaining why per-market origin numbers exist and which team
			    owns the contacts import — an argument, on a screen where the only useful move is to
			    go and make one. */}
			{/* An error is not an empty workspace. Both selects come from one request, so when it
			    fails both option lists are `[]` and the two lines below would state, as fact, that
			    this client has no numbers and no contacts. */}
			{isError ? (
				<Typography variant="body2" color="error.main">
					Could not load this workspace&rsquo;s numbers and contact groups. Reload to try again.
				</Typography>
			) : null}

			{!isError && !isNumbersPending && numberOptions.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No phone numbers in this workspace — provision one on{" "}
					<AccountPageLink accountId={options?.account?.id} path="/phone-numbers">
						Phone Numbers
					</AccountPageLink>
					.
				</Typography>
			) : null}

			{!isError && !isGroupsPending && groupOptions.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No contact groups in this workspace — import the lead list on{" "}
					<AccountPageLink accountId={options?.account?.id} path="/contacts/groups">
						Contacts
					</AccountPageLink>{" "}
					first.
				</Typography>
			) : null}

			<TableContainer sx={{ overflowX: "auto" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
							<TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Origin Number</TableCell>
							<TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Contact Group</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{markets.map((market) => {
							const provisioned = Boolean(market.campaignId);
							const busy = saving === market.id;
							const disabled = provisioned || busy;

							return (
								<TableRow key={market.id} hover>
									<TableCell>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{marketFlag(market.market)} {marketLabel(market.market)}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{market.timezone}
											{market.position === 0 ? " · lead market" : ""}
										</Typography>
									</TableCell>

									<TableCell>
										<HelpTooltip
											title={
												provisioned
													? "This market is already a campaign. Its origin number is the campaign's now — provisioning skips a market that has one, so changing it here would save and dial from the old number anyway."
													: "The local number this market dials from. Answer rates depend on it, which is why it is chosen per market rather than per request."
											}
										>
											<span>
												<TextField
													select
													size="small"
													fullWidth
													disabled={disabled || numberOptions.length === 0}
													// A table column header names this control for a sighted reader only.
													slotProps={{ htmlInput: { "aria-label": `Origin number for ${marketLabel(market.market)}` } }}
													value={market.originNumberId === null ? "" : String(market.originNumberId)}
													onChange={(event) =>
														void save(market, {
															// Empty string is the "none" row, and it has to reach the API as null:
															// an absent key is skipped by the update, so unbinding would silently
															// do nothing.
															originNumberId: event.target.value === "" ? null : Number(event.target.value),
														})
													}
												>
													<MenuItem value="">
														<Typography variant="body2" color="text.secondary">
															No number bound
														</Typography>
													</MenuItem>
													{numberOptions.map((number) => (
														<MenuItem key={number.id} value={String(number.id)}>
															{number.number}
															{number.countryCode ? ` · ${number.countryCode}` : ""}
														</MenuItem>
													))}
												</TextField>
											</span>
										</HelpTooltip>
									</TableCell>

									<TableCell>
										<HelpTooltip
											title={
												provisioned
													? "This market is already a campaign. Its contacts are the campaign's group now — a second provisioning run skips this market, so rebinding here would change nothing about what dials."
													: "The contact group this market’s campaign dials. Provisioning copies it onto the campaign; without one the campaign is built with nobody in it."
											}
										>
											<span>
												<TextField
													select
													size="small"
													fullWidth
													disabled={disabled || groupOptions.length === 0}
													slotProps={{ htmlInput: { "aria-label": `Contact group for ${marketLabel(market.market)}` } }}
													value={market.leadGroupId ?? ""}
													onChange={(event) =>
														void save(market, { leadGroupId: event.target.value === "" ? null : event.target.value })
													}
												>
													<MenuItem value="">
														<Typography variant="body2" color="text.secondary">
															No contacts loaded
														</Typography>
													</MenuItem>
													{groupOptions.map((group) => (
														<MenuItem key={group.id} value={group.id}>
															{group.name}
															{group.isStatic ? "" : " · rule-based"}
														</MenuItem>
													))}
												</TextField>
											</span>
										</HelpTooltip>
									</TableCell>

									<TableCell>
										{busy ? (
											<CircularProgress size={16} />
										) : provisioned ? (
											<Chip icon={<LockIcon />} label="Campaign built" size="small" variant="outlined" />
										) : market.originNumberId && market.leadGroupId ? (
											<Chip color="success" label="Ready to build" size="small" variant="soft" />
										) : (
											<Chip color="warning" label="Not bound" size="small" variant="soft" />
										)}
									</TableCell>
								</TableRow>
							);
						})}

						{markets.length === 0 ? (
							<TableRow>
								<TableCell colSpan={4}>
									<Typography variant="body2" color="text.secondary">
										This request has no markets, so there is nothing to bind.
									</Typography>
								</TableCell>
							</TableRow>
						) : null}
					</TableBody>
				</Table>
			</TableContainer>

			<Typography variant="caption" color="text.secondary">
				Bind a local number and a prepared contact group for every market before approval.
			</Typography>
		</Stack>
	);
}
